import { useTranslation } from 'react-i18next';
import {
  Control,
  FormState,
  useFieldArray,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import {
  Box,
  Divider,
  FormHelperText,
  Grid,
  Paper,
  styled,
  Typography,
} from '@mui/material';

import SelectMenu2 from '../../../form/SelectMenu2';
import CheckboxInput from '../../../form/CheckboxInput';
import TextInput2 from '../../../form/TextInput2';
import { TimeZone } from '../../../../data/models/timezone.model';
import validationUtils from '../../../../services/validationUtils';
import { TripPreferences } from '../../../../data/models/trip-preferences.model';
import ChipSelectMenu from '../../../form/ChipSelectMenu';
import { CoverageArea } from '../../../../data/models/coverage-area.model';

const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
    e.preventDefault();
  }
};

const TimeGridRow = ({
  checkName,
  startTimeName,
  endTimeName,
  checkLabel,
  startTimeLabel,
  endTimeLabel,
  control,
  formState,
  hours,
  isActive,
  startTime,
  endTime,
}: {
  checkName: string | null;
  startTimeName: string;
  endTimeName: string;
  checkLabel: string;
  startTimeLabel: string;
  endTimeLabel: string;
  control: Control<any>;
  formState: FormState<any>;
  hours: { time: string; label: string }[];
  isActive: boolean;
  startTime?: string;
  endTime?: string;
}) => {
  const startTimeIndex = hours.findIndex((hour) => hour.time === startTime);
  const endTimeIndex = hours.findIndex((hour) => hour.time === endTime);

  const startHours = hours.slice(0, endTimeIndex);
  const endHours = hours.slice(startTimeIndex + 1);

  return (
    <>
      <Grid
        item
        xs={isActive ? 5 : 16}
        height={60}
        alignItems="center"
        display="flex">
        {checkName ? (
          <CheckboxInput
            name={checkName}
            label={checkLabel}
            control={control}
          />
        ) : (
          <Typography>{checkLabel}</Typography>
        )}
      </Grid>

      {isActive && (
        <>
          <Grid item xs={5}>
            <SelectMenu2
              label={startTimeLabel}
              name={startTimeName}
              className={`${classes.hour}`}
              values={startHours}
              valueAttr="time"
              labelAttr="label"
              required={false}
              control={control}
              errors={formState.errors}
            />
          </Grid>
          <Grid item xs={1}>
            <Typography align="center">&ndash;</Typography>
          </Grid>
          <Grid item xs={5}>
            <SelectMenu2
              label={endTimeLabel}
              name={endTimeName}
              className={`${classes.hour}`}
              values={endHours}
              valueAttr="time"
              labelAttr="label"
              required={false}
              control={control}
              errors={formState.errors}
            />
          </Grid>
        </>
      )}
    </>
  );
};

const VehicleGridRow = ({
  index,
  label,
  countName,
  control,
  formState,
}: {
  index: number;
  label: string | undefined;
  countName: string;
  control: Control<any>;
  formState: FormState<any>;
}) => {
  const { t } = useTranslation('common');
  const errorMessage =
    // @ts-ignore
    formState.errors.vehicleTripModeCapacities?.[index]?.capacity?.message;

  return (
    <>
      <Grid item xs={5}>
        <Typography>{label}</Typography>
      </Grid>
      <Grid item xs={7}>
        <TextInput2
          label=""
          type="number"
          name={countName}
          required={true}
          control={control}
          inputProps={{
            min: 0,
            step: 1,
            onKeyDown: handleKeyDown,
          }}
          rules={{
            validate: (value) => value >= 0 || t('common.nonNegativeValue'),
          }}
        />
      </Grid>
      <Grid item xs={5} />
      <Grid item xs={7}>
        <Typography color="#d32f2f" fontSize={12}>
          {errorMessage}
        </Typography>
      </Grid>
    </>
  );
};

const PREFIX = 'TripPreferencesForm';

const classes = {
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
  grid: `${PREFIX}-grid`,
  service: `${PREFIX}-service`,
  divider: `${PREFIX}-divider`,
  hour: `${PREFIX}-hour`,
  timeZone: `${PREFIX}-timeZone`,
  trips: `${PREFIX}-trips`,
  vspace: `${PREFIX}-vspace`,
  bitterFont: `${PREFIX}-bitterFont`,
  errorMessage: `${PREFIX}-errorMessage`,
  hoursOfOperations: `${PREFIX}-hoursOfOperations`,
};

const Root = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    marginLeft: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    overflowY: 'auto',
    width: 550,
    height: 'fit-content',
  },

  [`& .${classes.title}`]: {
    fontSize: 21,
    fontFamily: 'Bitter',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    textTransform: 'capitalize',
  },

  [`& .${classes.grid}`]: {
    width: 500,
  },

  [`& .${classes.service}`]: {
    width: 500,
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.divider}`]: {
    marginBottom: theme.spacing(1),
  },

  [`& .${classes.hour}`]: {
    width: '100%',
  },

  [`& .${classes.trips}`]: {
    width: 100,
  },

  [`& .${classes.timeZone}`]: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: 250,
  },

  [`& .${classes.vspace}`]: {
    marginTop: theme.spacing(4),
  },

  [`& .${classes.bitterFont}`]: {
    fontFamily: 'Bitter',
  },

  [`& .${classes.errorMessage}`]: {
    color: theme.palette.error.main,
  },

  [`& .${classes.hoursOfOperations}.${classes.errorMessage}`]: {
    // outline: theme.palette.error.main + ' solid 2px',
  },
}));

interface TripPreferencesFormProps {
  watch: UseFormWatch<TripPreferences>;
  control: Control<TripPreferences>;
  formState: FormState<TripPreferences>;
  setValue: UseFormSetValue<TripPreferences>;
  hours: { time: string; label: string }[];
  sameHoursAllDays?: boolean;
  timeZones: TimeZone[];
  coverageAreas: CoverageArea[];
  hasTitle?: boolean;
}

export default function TripPreferencesForm({
  watch,
  control,
  formState,
  setValue,
  hours,
  sameHoursAllDays = false,
  timeZones,
  coverageAreas,
  hasTitle,
}: TripPreferencesFormProps) {
  const { t } = useTranslation('common');
  const startLabel = t('preferences.startTime');
  const endLabel = t('preferences.endTime');
  const requiredFieldMessage = t('common.requiredField');
  const { fields: hoursOfOperationFields } = useFieldArray({
    control,
    name: 'hoursOfOperations',
    rules: {
      required: true,
      validate: (values) => {
        let isValid = false;
        values.forEach((hours) => {
          if (hours.isActive) {
            isValid = true;
          }
        });
        return sameHoursAllDays ? true : isValid;
      },
    },
  });

  const { fields: vehicleTripModesCapacityFields } = useFieldArray({
    control,
    name: 'vehicleTripModeCapacities',
  });

  return (
    <Root
      className={classes.container}
      style={{
        marginLeft: hasTitle ? 'auto' : 0,
        marginRight: hasTitle ? 20 : 0,
      }}>
      {hasTitle && (
        <Typography variant="h4" className={classes.bitterFont} mb={2}>
          {t('onboard.tripPrefInfo')}
        </Typography>
      )}
      <Typography className={classes.title}>
        {t('providers.serviceArea')}
      </Typography>
      <ChipSelectMenu
        label={t('provider.locations')}
        name="coverageAreas"
        values={coverageAreas}
        valueAttr="coverageAreaID"
        labelAttr="coverageAreaName"
        required={true}
        control={control}
        errors={formState.errors}
        rules={{ required: requiredFieldMessage }}
        showOkCancel={true}
      />
      <Typography className={`${classes.title} ${classes.vspace}`}>
        {t('preferences.hours')}
      </Typography>

      <Box className={`${classes.hoursOfOperations}`}>
        {formState.errors.hoursOfOperations && (
          <FormHelperText className={`${classes.errorMessage}`}>
            {t('preferences.hoursOfOperationsRequired')}
          </FormHelperText>
        )}
        <CheckboxInput
          name="sameHoursAllDays"
          label={t('preferences.sameHoursAllDays')}
          control={control}
        />
        <SelectMenu2
          label={t('preferences.timeZone')}
          name="timeZoneID"
          className={classes.timeZone}
          values={timeZones}
          valueAttr="timeZoneID"
          labelAttr="timeZoneName"
          control={control}
          errors={formState.errors}
          required={true}
        />
        <Grid
          container
          className={classes.grid}
          columns={16}
          columnSpacing={1}
          justifyContent="center"
          alignItems="center"
          rowGap={1}>
          <Grid item xs={16}>
            <Typography>{t('preferences.daysAvailable')}</Typography>
          </Grid>
          <Grid item xs={16}>
            <Divider className={classes.divider} />
          </Grid>
          {sameHoursAllDays ? (
            <TimeGridRow
              checkName={null}
              startTimeName="allDaysStartServiceOn"
              endTimeName="allDaysEndServiceOn"
              startTime={watch('allDaysStartServiceOn')}
              endTime={watch('allDaysEndServiceOn')}
              checkLabel={t('preferences.allDays')}
              startTimeLabel={startLabel}
              endTimeLabel={endLabel}
              control={control}
              formState={formState}
              hours={hours}
              isActive
            />
          ) : (
            <>
              {hoursOfOperationFields.map((field, index) => (
                <TimeGridRow
                  key={field.id}
                  isActive={
                    watch(`hoursOfOperations.${index}.isActive`) ?? false
                  }
                  checkName={`hoursOfOperations.${index}.isActive`}
                  startTime={
                    watch(`hoursOfOperations.${index}.startServiceOn`) ?? ''
                  }
                  startTimeName={`hoursOfOperations.${index}.startServiceOn`}
                  endTime={
                    watch(`hoursOfOperations.${index}.endServiceOn`) ?? ''
                  }
                  endTimeName={`hoursOfOperations.${index}.endServiceOn`}
                  checkLabel={watch(
                    `hoursOfOperations.${index}.frequencyDayOfWeek.frequencyDayOfWeekName`,
                  )}
                  startTimeLabel={startLabel}
                  endTimeLabel={endLabel}
                  control={control}
                  formState={formState}
                  hours={hours}
                />
              ))}
            </>
          )}
        </Grid>
      </Box>
      <Typography className={`${classes.title} ${classes.vspace}`}>
        {t('preferences.vehicles')}
      </Typography>
      <Grid
        container
        className={classes.grid}
        columnSpacing={1}
        justifyContent="center"
        alignItems="center"
        rowGap={1}>
        <Grid item xs={5}>
          <Typography>{t('preferences.vehicleTypes')}</Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography>{t('preferences.vehicleNumbers')}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Divider className={classes.divider} />
        </Grid>
        {vehicleTripModesCapacityFields &&
          vehicleTripModesCapacityFields.map((field, index) => (
            <VehicleGridRow
              key={field.id}
              label={watch(
                `vehicleTripModeCapacities.${index}.tripMode.tripModeName`,
              )}
              countName={`vehicleTripModeCapacities.${index}.capacity`}
              control={control}
              formState={formState}
              index={index}
            />
          ))}
      </Grid>
      <Typography className={classes.vspace}>
        {t('preferences.howManyTrips')}
      </Typography>
      <Divider />
      <TextInput2
        label=""
        className={`${classes.trips} ${classes.vspace}`}
        type="number"
        name="capacityOfTripsToServiceInDay"
        required={true}
        control={control}
        errors={formState.errors}
        inputProps={{
          min: 1,
          max: 1000,
          step: 1,
          onKeyDown: handleKeyDown,
        }}
        rules={{
          validate: validationUtils.rangeValidation(1, 1000, t),
        }}
      />
    </Root>
  );
}
